import { useState, useEffect, useCallback } from 'react';
import { opportunityService } from '@/services/opportunityService';
import type { Opportunity, OpportunityApplication } from '@/types';
import { useUnifiedAuth } from '@/contexts/UnifiedAuthContext';

export function useOpportunities(filters?: {
  vehicle_category?: string;
  zone_name?: string;
  priority?: string;
}) {
  const { user } = useUnifiedAuth();
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [applications, setApplications] = useState<OpportunityApplication[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetch = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    try {
      setLoading(true);
      const [oppResult, apps] = await Promise.all([
        opportunityService.list(filters),
        opportunityService.myApplications(),
      ]);
      setOpportunities(oppResult.data);
      setTotal(oppResult.total);
      setApplications(apps);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error loading opportunities');
    } finally {
      setLoading(false);
    }
  }, [user, filters]);

  useEffect(() => { fetch(); }, [fetch]);

  const apply = useCallback(async (opportunityId: string, data: { vehicle_id?: string; notes?: string }) => {
    const app = await opportunityService.apply(opportunityId, data);
    setApplications(prev => [app, ...prev]);
    return app;
  }, []);

  return { opportunities, applications, total, loading, error, refetch: fetch, apply };
}
